import { ActionsRenderCell } from '@/components/bulkQueue/cells/ActionsRender';
import { FulfillmentWindowRenderCell } from '@/components/bulkQueue/cells/FulfillmentWindowRender';
import { OrderRenderCell } from '@/components/bulkQueue/cells/OrderRender';
import { PatientRenderCell } from '@/components/bulkQueue/cells/PatientRender';
import { PriorityRenderCell } from '@/components/bulkQueue/cells/PriorityRender';
import { ClinicianRenderCell } from '@/components/bulkQueue/cells/clinician/ClinicianRender';
import {
    DateSelectionCell,
    DateSelectionHeader,
} from '@/components/bulkQueue/cells/date/DateSelection';
import { ServicesRenderCell } from '@/components/bulkQueue/cells/services/ServicesRender';
import { getSetCellProps } from '@/components/bulkQueue/tables/components';
import { VisitTableRow } from '@/components/bulkQueue/tables/queries';
import { BulkTentativeDateAssignmentTableQuery } from '@/components/bulkQueue/tables/tentativeDate/BulkTentativeDateAssignmentTable.gql.types';
import { BulkTentativeDateContextProvider } from '@/components/bulkQueue/tables/tentativeDate/BulkTentativeDateContext';
import { useConnectedBulkTable } from '@/components/bulkQueue/tables/useConnectedBulkTable';
import { Pagination } from '@/components/bulkQueue/tables/useUnifiedPagination';
import { BulkTableRow } from '@/components/bulkQueue/types';
import { rowToLink } from '@/components/bulkQueue/utils';
import { MapFriendlyVisit } from '@/components/mapping/VisitsMap/VisitsMap.types';
import { useSetMapFriendlyVisits } from '@/components/mapping/VisitsMap/useSetMapFriendlyVisits';
import { AxleTable, RawColumn, ServerSideSort } from '@/components/tables/AxleTable';
import { visitOrVisitRequestTableRowFragmentToMapFriendlyVisits } from '@/components/visits/utils/visitOrVisitRequestTableRowFragmentToMapFriendlyVisits';
import { PAGE_BODY_SKELETON_HEIGHT, SimpleSkeleton } from '@/fetching/suspense';
import { gql } from '@apollo/client';
import { Typography } from '@mui/material';
import { FC, useCallback } from 'react';

export const BULK_TENTATIVE_DATE_ASSIGNMENT_TABLE_QUERY = gql`
    query BulkTentativeDateAssignmentTable(
        $visitFilters: VisitFilter
        $visitPagination: PaginationInput
        $visitOrdering: VisitOrdering
    ) {
        visits(filters: $visitFilters, pagination: $visitPagination, ordering: $visitOrdering) {
            totalCount
            items {
                ...VisitTableRow
            }
        }
    }
    ${VisitTableRow}
`;

type BulkTentativeDateAssignmentTableProps = {
    rows: BulkTableRow[];
    pagination: Pagination;
    title?: string;
    serverSideSort: ServerSideSort;
};

const COLUMNS: RawColumn<BulkTableRow>[] = [
    {
        field: 'sortPriority',
        label: 'Priority',
        rowToComponent: (row) => <PriorityRenderCell entity={row} />,
        options: { sort: false },
    },
    {
        field: 'patient.fullName',
        label: 'Patient',
        rowToComponent: (row) => <PatientRenderCell entity={row} />,
    },
    {
        field: 'sortServicesName',
        label: 'Services',
        rowToComponent: (row) => <ServicesRenderCell entity={row} />,
        options: { sort: false },
    },
    {
        field: 'cliniciansSortKey',
        label: 'Clinician',
        rowToComponent: (row) => <ClinicianRenderCell entity={row} />,
        options: { sort: false },
    },
    {
        field: 'sortDate',
        label: 'Date',
        rowToComponent: (row) => <DateSelectionCell entity={row} />,
        options: {
            setCellProps: () => getSetCellProps(true),
            customHeadRender: (columnMeta, handleToggleColumn, sortOrder) => (
                <DateSelectionHeader
                    key={columnMeta.name + '-header'}
                    columnMeta={columnMeta}
                    handleToggleColumn={handleToggleColumn}
                    sortOrder={sortOrder}
                />
            ),
        },
    },
    {
        field: 'fulfillmentWindow.startDate',
        label: 'Fulfillment Window',
        rowToComponent: (row) => <FulfillmentWindowRenderCell entity={row} />,
    },
    {
        field: 'orderSortKey',
        label: 'Order',
        rowToComponent: (row) => <OrderRenderCell entity={row} />,
        options: { sort: false },
    },
    {
        field: '',
        label: 'Actions',
        rowToComponent: (row) => <ActionsRenderCell entity={row} />,
    },
];

export const BulkTentativeDateAssignmentTable: FC<BulkTentativeDateAssignmentTableProps> = (
    props,
) => {
    const { rows, pagination, title, serverSideSort } = props;

    return (
        <AxleTable
            title={
                typeof title === 'string' ? (
                    <Typography variant='h2'>{title}</Typography>
                ) : undefined
            }
            dynamicPaging={pagination}
            data={rows}
            toolbar={false}
            rowToLink={rowToLink}
            columns={COLUMNS}
            serverSideSort={serverSideSort}
        />
    );
};

type ConnectedBulkTentativeDateAssignmentTableProps = {
    title?: string;
};

export const ConnectedBulkTentativeDateAssignmentTable: FC<
    ConnectedBulkTentativeDateAssignmentTableProps
> = (props) => {
    const { title } = props;
    const { rows, pagination, serverSideSort, showSkeleton } =
        useConnectedBulkTable<BulkTentativeDateAssignmentTableQuery>({
            query: BULK_TENTATIVE_DATE_ASSIGNMENT_TABLE_QUERY,
        });

    const setMapFriendlyVisits: () => MapFriendlyVisit[] = useCallback(() => {
        return visitOrVisitRequestTableRowFragmentToMapFriendlyVisits(rows);
    }, [rows]);

    useSetMapFriendlyVisits(setMapFriendlyVisits);

    if (showSkeleton) {
        return <SimpleSkeleton height={PAGE_BODY_SKELETON_HEIGHT} />;
    }

    return (
        <BulkTentativeDateContextProvider entities={rows}>
            <BulkTentativeDateAssignmentTable
                rows={rows}
                title={title}
                pagination={pagination}
                serverSideSort={serverSideSort}
            />
        </BulkTentativeDateContextProvider>
    );
};
